import React, { useMemo, useState } from 'react';
import { Info, Minus, Plus, RotateCcw } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { CostCategory, CostTariffs } from '../interfaces/costs.interface';
import { categoryLabel, chargeForDays, fmtMoney, fmtTotals, sumByCurrency, type Size } from '../utils/costs';

interface CostCalculatorProps {
  tariffs?: CostTariffs;
  isLoading: boolean;
}

const SIZES: Size[] = ['20', '40', '45'];
const DEFAULTS = { size: '40' as Size, category: 'standard' as CostCategory, count: 1, terminalDays: 5, returnDays: 10, freight: '' };

/** Categoria de terminal pentru o categorie de locale (OOG = echipament special). */
const terminalCategory = (c: CostCategory): CostCategory => (c === 'oog_small' || c === 'oog_large' ? 'special' : c);

function Stepper({
  value,
  onChange,
  min,
  max,
  label,
  id,
}: {
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  label: string;
  id: string;
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, Number.isFinite(n) ? Math.round(n) : min));
  // Ciorna permite tastarea liberă („2” → „20”); valoarea se limitează doar la ieșirea din câmp.
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft !== null) onChange(clamp(draft === '' ? min : Number(draft)));
    setDraft(null);
  };
  return (
    <div className="cst-stepper">
      <button type="button" aria-label={`${label} −`} onClick={() => onChange(clamp(value - 1))} disabled={value <= min}>
        <Minus size={14} />
      </button>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        aria-label={label}
        value={draft ?? String(value)}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '').slice(0, 3);
          setDraft(digits);
          if (digits !== '' && Number(digits) >= min && Number(digits) <= max) onChange(Number(digits));
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
      <button type="button" aria-label={`${label} +`} onClick={() => onChange(clamp(value + 1))} disabled={value >= max}>
        <Plus size={14} />
      </button>
    </div>
  );
}

/** Bara zilelor: verde = zile libere, galben = zile plătite. */
function DaysBar({ days, freeDays, max }: { days: number; freeDays: number; max: number }) {
  const free = Math.min(days, freeDays);
  const paid = Math.max(0, days - freeDays);
  return (
    <div className="cst-daysbar" aria-hidden>
      <span className="free" style={{ width: `${(free / max) * 100}%` }} />
      <span className="paid" style={{ width: `${(paid / max) * 100}%` }} />
    </div>
  );
}

const CostCalculator: React.FC<CostCalculatorProps> = ({ tariffs, isLoading }) => {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';
  const [size, setSize] = useState<Size>(DEFAULTS.size);
  const [category, setCategory] = useState<CostCategory>(DEFAULTS.category);
  const [count, setCount] = useState(DEFAULTS.count);
  const [terminalDays, setTerminalDays] = useState(DEFAULTS.terminalDays);
  const [returnDays, setReturnDays] = useState(DEFAULTS.returnDays);
  const [freight, setFreight] = useState(DEFAULTS.freight);

  const reset = () => {
    setSize(DEFAULTS.size);
    setCategory(DEFAULTS.category);
    setCount(DEFAULTS.count);
    setTerminalDays(DEFAULTS.terminalDays);
    setReturnDays(DEFAULTS.returnDays);
    setFreight(DEFAULTS.freight);
  };

  const categories = useMemo<CostCategory[]>(
    () => (tariffs?.locale?.rows.map((r) => r.category) as CostCategory[] | undefined) ?? ['standard', 'imo', 'reefer', 'special'],
    [tariffs],
  );

  const result = useMemo(() => {
    if (!tariffs) return null;
    const row = tariffs.locale?.rows.find((r) => r.category === category);
    const thcUnit = row ? (size === '20' ? row.price20 : size === '45' ? row.price45 : row.price40) : null;
    const tCat = terminalCategory(category);
    const pick = (p: typeof tariffs.demurrage) => p?.categories.find((c) => c.category === tCat) ?? p?.categories.find((c) => c.category === 'standard');
    const dem = pick(tariffs.demurrage);
    const sto = pick(tariffs.storage);
    const effReturn = Math.max(returnDays, terminalDays);
    const stoCharge = sto ? chargeForDays(terminalDays, sto.freeDays, sto.tiers, size) : null;
    const demCharge = dem ? chargeForDays(effReturn, dem.freeDays, dem.tiers, size) : null;
    const freightUnit = Number(String(freight).replace(',', '.'));
    const hasFreight = freight !== '' && Number.isFinite(freightUnit) && freightUnit > 0;
    const localeCur = tariffs.locale?.currency || 'EUR';
    const terminalCur = tariffs.demurrage?.currency || tariffs.storage?.currency || 'EUR';

    const lines = [
      hasFreight && { key: 'freight', label: isRo ? 'Navlu' : 'Ocean freight', unit: freightUnit, total: freightUnit * count, currency: 'USD', note: '' },
      thcUnit != null && {
        key: 'thc',
        label: isRo ? 'Cheltuieli locale (THC)' : 'Local charges (THC)',
        unit: thcUnit,
        total: thcUnit * count,
        currency: localeCur,
        note: '',
      },
      stoCharge && sto && {
        key: 'sto',
        label: isRo ? 'Depozitare' : 'Storage',
        unit: stoCharge.amount,
        total: stoCharge.amount * count,
        currency: terminalCur,
        note:
          stoCharge.billable > 0
            ? isRo
              ? `${stoCharge.billable} ${stoCharge.billable === 1 ? 'zi' : 'zile'} peste cele ${sto.freeDays} libere`
              : `${stoCharge.billable} day${stoCharge.billable === 1 ? '' : 's'} over ${sto.freeDays} free`
            : isRo
              ? `în cele ${sto.freeDays} zile libere`
              : `within ${sto.freeDays} free days`,
      },
      demCharge && dem && {
        key: 'dem',
        label: isRo ? 'Contrastalii' : 'Demurrage',
        unit: demCharge.amount,
        total: demCharge.amount * count,
        currency: terminalCur,
        note:
          demCharge.billable > 0
            ? isRo
              ? `${demCharge.billable} ${demCharge.billable === 1 ? 'zi' : 'zile'} peste cele ${dem.freeDays} libere`
              : `${demCharge.billable} day${demCharge.billable === 1 ? '' : 's'} over ${dem.freeDays} free`
            : isRo
              ? `în cele ${dem.freeDays} zile libere`
              : `within ${dem.freeDays} free days`,
      },
    ].filter(Boolean) as { key: string; label: string; unit: number; total: number; currency: string; note: string }[];

    return {
      lines,
      totals: sumByCurrency(lines.map((l) => ({ amount: l.total, currency: l.currency }))),
      thcMissing: thcUnit == null,
      dem,
      sto,
      effReturn,
    };
  }, [tariffs, category, size, count, terminalDays, returnDays, freight, isRo]);

  if (isLoading || !tariffs || !result) {
    return (
      <div className="card" style={{ padding: 0 }}>
        <div className="empty">
          <div className="fw-600">{isRo ? 'Se încarcă tarifele…' : 'Loading tariffs…'}</div>
        </div>
      </div>
    );
  }

  const maxDays = Math.max(result.effReturn, terminalDays, result.dem?.freeDays ?? 0, result.sto?.freeDays ?? 0, 1);

  return (
    <>
      <div className="cst-calc">
        <div className="card card-pad cst-calc-form">
          <div className="cst-calc-head">
            <div>
              <div className="fw-600">{isRo ? 'Estimați costul unui transport' : 'Estimate a shipment'}</div>
              <div className="muted fs-12">{isRo ? 'Pe baza tarifelor standard în vigoare' : 'Based on the current standard tariffs'}</div>
            </div>
            <button type="button" className="btn btn-sm btn-ghost" onClick={reset}>
              <RotateCcw size={13} />
              {isRo ? 'Resetează' : 'Reset'}
            </button>
          </div>

          <div className="cst-field">
            <span className="cst-label">{isRo ? 'Mărime container' : 'Container size'}</span>
            <div className="cst-seg" role="radiogroup" aria-label={isRo ? 'Mărime container' : 'Container size'}>
              {SIZES.map((s) => (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={size === s}
                  className={size === s ? 'active' : ''}
                  onClick={() => setSize(s)}
                >
                  {s}'
                </button>
              ))}
            </div>
          </div>

          <div className="cst-field">
            <label className="cst-label" htmlFor="cst-category">
              {isRo ? 'Tip container / marfă' : 'Container / cargo type'}
            </label>
            <select
              id="cst-category"
              className="select cst-select"
              value={category}
              onChange={(e) => setCategory(e.target.value as CostCategory)}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {categoryLabel(c, lang)}
                </option>
              ))}
            </select>
          </div>

          <div className="cst-field">
            <label className="cst-label" htmlFor="cst-count">
              {isRo ? 'Număr de containere' : 'Number of containers'}
            </label>
            <Stepper id="cst-count" value={count} onChange={setCount} min={1} max={200} label={isRo ? 'Containere' : 'Containers'} />
          </div>

          <div className="cst-field-row">
            <div className="cst-field">
              <label className="cst-label" htmlFor="cst-terminal">
                {isRo ? 'Zile în terminal' : 'Days in terminal'}
              </label>
              <Stepper
                id="cst-terminal"
                value={terminalDays}
                onChange={(n) => {
                  setTerminalDays(n);
                  if (returnDays < n) setReturnDays(n);
                }}
                min={1}
                max={120}
                label={isRo ? 'Zile în terminal' : 'Days in terminal'}
              />
              <span className="cst-help">{isRo ? 'de la descărcare până la ridicare' : 'from discharge to pick-up'}</span>
            </div>
            <div className="cst-field">
              <label className="cst-label" htmlFor="cst-return">
                {isRo ? 'Zile până la retur' : 'Days until return'}
              </label>
              <Stepper
                id="cst-return"
                value={Math.max(returnDays, terminalDays)}
                onChange={setReturnDays}
                min={terminalDays}
                max={180}
                label={isRo ? 'Zile până la retur' : 'Days until return'}
              />
              <span className="cst-help">{isRo ? 'până la returnarea containerului gol' : 'until the empty container is returned'}</span>
            </div>
          </div>

          <div className="cst-field">
            <label className="cst-label" htmlFor="cst-freight">
              {isRo ? 'Navlu / container (opțional)' : 'Freight / container (optional)'}
            </label>
            <div className="cst-money-input">
              <input
                id="cst-freight"
                type="text"
                inputMode="decimal"
                placeholder={isRo ? 'ex. 2.500' : 'e.g. 2,500'}
                value={freight}
                onChange={(e) => setFreight(e.target.value.replace(/[^\d.,]/g, ''))}
              />
              <span>USD</span>
            </div>
            <span className="cst-help">{isRo ? 'din oferta primită de la noi' : 'from the offer you received from us'}</span>
          </div>
        </div>

        <div className="card cst-calc-result">
          <div className="card-header">
            <div>
              <div className="title">{isRo ? 'Cost estimat' : 'Estimated cost'}</div>
              <div className="sub">
                {count} × {size}' · {categoryLabel(category, lang)}
              </div>
            </div>
          </div>
          <div className="cst-lines">
            {result.lines.map((l) => (
              <div key={l.key} className="cst-line">
                <div className="cst-line-l">
                  <div className="fw-500">{l.label}</div>
                  <div className="muted fs-12">
                    {count > 1 && `${count} × ${fmtMoney(l.unit, lang, l.unit % 1 ? 2 : 0)} ${l.currency}`}
                    {count > 1 && l.note && ' · '}
                    {l.note}
                  </div>
                </div>
                <div className={'cst-line-v mono' + (l.total === 0 ? ' zero' : '')}>
                  {fmtMoney(l.total, lang, l.total % 1 ? 2 : 0)} {l.currency}
                </div>
              </div>
            ))}
            {result.thcMissing && (
              <div className="cst-line warn fs-12">
                {isRo ? `Nu există tarif de locale pentru ${size}' la această categorie.` : `No local-charge tariff for ${size}' in this category.`}
              </div>
            )}
          </div>
          <div className="cst-total">
            <span>{isRo ? 'Total estimat' : 'Estimated total'}</span>
            <span className="mono">{fmtTotals(result.totals, lang)}</span>
          </div>

          {(result.sto || result.dem) && (
            <div className="cst-timeline">
              {result.sto && (
                <div className="cst-tl-row">
                  <span className="k">{isRo ? 'Depozitare' : 'Storage'}</span>
                  <DaysBar days={terminalDays} freeDays={result.sto.freeDays} max={maxDays} />
                  <span className="v">
                    {terminalDays} {isRo ? 'zile' : 'days'}
                  </span>
                </div>
              )}
              {result.dem && (
                <div className="cst-tl-row">
                  <span className="k">{isRo ? 'Contrastalii' : 'Demurrage'}</span>
                  <DaysBar days={result.effReturn} freeDays={result.dem.freeDays} max={maxDays} />
                  <span className="v">
                    {result.effReturn} {isRo ? 'zile' : 'days'}
                  </span>
                </div>
              )}
              <div className="cst-tl-legend">
                <span>
                  <i className="free" /> {isRo ? 'zile libere' : 'free days'}
                </span>
                <span>
                  <i className="paid" /> {isRo ? 'zile plătite' : 'paid days'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="cst-note">
        <Info size={15} />
        <div>
          {isRo
            ? 'Calculul folosește tarifele standard, fără TVA. Ofertele cu prețuri sau zile libere negociate pot duce la alte valori. Taxele de transport terestru și vămuire nu sunt incluse.'
            : 'The calculation uses the standard tariffs, excluding VAT. Offers with negotiated prices or free days may differ. Inland transport and customs fees are not included.'}
        </div>
      </div>
    </>
  );
};

export default CostCalculator;
